import type { SupabaseClient } from '@supabase/supabase-js';

import { z } from 'zod';

import { getLogger } from '@kit/shared/logger';

/**
 * Consent types matching the database enum public.consent_type.
 */
export const ConsentTypeSchema = z.enum([
  'opt_in',
  'explicit',
  'legitimate_interest',
  'contract_necessity',
]);

export type ConsentType = z.infer<typeof ConsentTypeSchema>;

/**
 * Compliance jurisdictions matching the database enum public.compliance_jurisdiction.
 */
export const ComplianceJurisdictionSchema = z.enum([
  'us-tcpa',
  'eu-gdpr',
  'ch-dsg',
  'ch-dsg-health',
  'za-popia',
  'ng-ndpr',
  'ke-dpa',
  'br-lgpd',
  'in-dpdp',
  'ph-dpa',
]);

export type ComplianceJurisdiction = z.infer<
  typeof ComplianceJurisdictionSchema
>;

/**
 * Outreach channels matching the database enum public.outreach_channel.
 */
export const OutreachChannelSchema = z.enum([
  'email',
  'sms',
  'whatsapp',
  'phone',
  'instagram',
]);

export type OutreachChannel = z.infer<typeof OutreachChannelSchema>;

/**
 * Channel-scope tokens stored in `consent_records.channel_scope`. Encodes
 * the Meta Rail 6 requirement that marketing-category sends opt in
 * separately from service-category replies, per
 * `docs/architecture/waba-guardrails-policy.md` §1 Rail 6.
 *
 * Shape is `<channel>_<category>`. We only enumerate the combinations
 * we currently support; the DB column is `text[]` so future scopes
 * (e.g. `instagram_marketing`) can be added without a migration.
 */
export const ChannelScopeSchema = z.enum([
  'whatsapp_service',
  'whatsapp_marketing',
  'sms_service',
  'sms_marketing',
  'email_service',
  'email_marketing',
]);

export type ChannelScope = z.infer<typeof ChannelScopeSchema>;

/**
 * Input schema for recording a consent event.
 */
export const RecordConsentSchema = z.object({
  leadId: z.string().uuid(),
  accountId: z.string().uuid(),
  consentType: ConsentTypeSchema,
  jurisdiction: ComplianceJurisdictionSchema,
  channel: OutreachChannelSchema,
  consentText: z.string().min(1),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
  expiresAt: z.string().datetime().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  /**
   * Explicit list of channel+category scopes the user consented to.
   * Used by the compliance gate to enforce WABA Rail 6 (MARKETING-
   * category sends require `<channel>_marketing` in this array).
   * Defaults to service-only at capture time when omitted.
   */
  channelScope: z.array(ChannelScopeSchema).optional(),
  /**
   * Exact business name the consent text referenced. Meta's messaging
   * policy requires consent to name the business, not a generic label
   * like "the business".
   */
  businessNamed: z.string().optional(),
});

export type RecordConsentParams = z.infer<typeof RecordConsentSchema>;

/**
 * Input schema for checking consent.
 */
export const CheckConsentSchema = z.object({
  leadId: z.string().uuid(),
  accountId: z.string().uuid(),
  channel: OutreachChannelSchema,
});

export type CheckConsentParams = z.infer<typeof CheckConsentSchema>;

const NAMESPACE = 'compliance:consent';

/**
 * Record a consent event. Append-only insert via admin client.
 * The consent_records table forbids UPDATE and DELETE at the database level.
 */
export async function recordConsent(
  adminClient: SupabaseClient,
  params: RecordConsentParams,
) {
  const logger = await getLogger();

  const ctx = { namespace: NAMESPACE, leadId: params.leadId };

  logger.info(ctx, 'Recording consent event');

  const parsed = RecordConsentSchema.parse(params);

  // Rail 6: the compliance gate reads channel_scope / business_named to
  // enforce MARKETING-category opt-in. Rows written via this path must
  // carry the explicit scope. Omitted -> service-only fallback, matching
  // the migration backfill for legacy rows.
  const channelScope =
    parsed.channelScope ?? defaultServiceScope(parsed.channel);

  const { data, error } = await adminClient
    .from('consent_records')
    .insert({
      lead_id: parsed.leadId,
      account_id: parsed.accountId,
      consent_type: parsed.consentType,
      jurisdiction: parsed.jurisdiction,
      channel: parsed.channel,
      consent_text: parsed.consentText,
      ip_address: parsed.ipAddress ?? null,
      user_agent: parsed.userAgent ?? null,
      expires_at: parsed.expiresAt ?? null,
      metadata: parsed.metadata ?? {},
      channel_scope: channelScope,
      business_named: parsed.businessNamed ?? null,
    })
    .select()
    .single();

  if (error) {
    logger.error({ ...ctx, error }, 'Failed to record consent event');
    throw new Error(`Failed to record consent: ${error.message}`);
  }

  logger.info(
    { ...ctx, consentId: data.id },
    'Consent event recorded successfully',
  );

  return data;
}

/**
 * Check whether a valid (non-expired) consent exists for a lead on a given channel.
 * Returns true only if at least one valid consent record exists.
 */
export async function checkConsent(
  adminClient: SupabaseClient,
  params: CheckConsentParams,
): Promise<boolean> {
  const logger = await getLogger();

  const ctx = {
    namespace: NAMESPACE,
    leadId: params.leadId,
    channel: params.channel,
  };

  logger.info(ctx, 'Checking consent for lead');

  const parsed = CheckConsentSchema.parse(params);

  const now = new Date();

  const { data, error } = await adminClient
    .from('consent_records')
    .select('id, expires_at')
    .eq('lead_id', parsed.leadId)
    .eq('account_id', parsed.accountId)
    .eq('channel', parsed.channel)
    .limit(100);

  if (error) {
    logger.error({ ...ctx, error }, 'Failed to check consent');
    // Fail-closed: if we cannot verify consent, treat as no consent
    return false;
  }

  if (!data || data.length === 0) {
    logger.info(ctx, 'No consent records found for lead and channel');
    return false;
  }

  // Check if at least one consent record is still valid (not expired)
  const hasValidConsent = data.some((record) => {
    if (!record.expires_at) {
      return true; // No expiry means consent is perpetual
    }
    return new Date(record.expires_at) > now;
  });

  logger.info({ ...ctx, hasValidConsent }, 'Consent check completed');

  return hasValidConsent;
}

/**
 * Retrieve the full consent history for a lead, ordered by most recent first.
 */
export async function getConsentHistory(
  adminClient: SupabaseClient,
  leadId: string,
  accountId: string,
) {
  const logger = await getLogger();

  const ctx = { namespace: NAMESPACE, leadId, accountId };

  logger.info(ctx, 'Fetching consent history');

  const parsedLeadId = z.string().uuid().parse(leadId);
  const parsedAccountId = z.string().uuid().parse(accountId);

  const { data, error } = await adminClient
    .from('consent_records')
    .select('*')
    .eq('lead_id', parsedLeadId)
    .eq('account_id', parsedAccountId)
    .order('created_at', { ascending: false });

  if (error) {
    logger.error({ ...ctx, error }, 'Failed to fetch consent history');
    throw new Error(`Failed to fetch consent history: ${error.message}`);
  }

  logger.info(
    { ...ctx, recordCount: data.length },
    'Consent history retrieved',
  );

  return data;
}

/**
 * Default channel_scope value when consent is captured without an
 * explicit scope. Always service-only — we never assume marketing
 * consent from a missing field per WABA Rail 6.
 */
export function defaultServiceScope(channel: OutreachChannel): ChannelScope[] {
  switch (channel) {
    case 'whatsapp':
      return ['whatsapp_service'];
    case 'sms':
      return ['sms_service'];
    case 'email':
      return ['email_service'];
    default:
      // phone / instagram — Rail 6 only applies to WhatsApp today but
      // we still return an empty array rather than guessing a scope.
      return [];
  }
}

/**
 * Map an outreach channel to its MARKETING-scope token.
 *
 * Returns `null` for channels where Rail 6 does not apply (phone,
 * instagram). The compliance gate treats a `null` return as
 * "rail doesn't apply" and allows the send.
 */
export function marketingScopeFor(
  channel: OutreachChannel,
): ChannelScope | null {
  switch (channel) {
    case 'whatsapp':
      return 'whatsapp_marketing';
    case 'sms':
      return 'sms_marketing';
    case 'email':
      return 'email_marketing';
    default:
      return null;
  }
}

/**
 * Input schema for the Rail 6 marketing-scope check.
 */
export const CheckMarketingScopeSchema = z.object({
  leadId: z.string().uuid(),
  accountId: z.string().uuid(),
  channel: OutreachChannelSchema,
});

export type CheckMarketingScopeParams = z.infer<
  typeof CheckMarketingScopeSchema
>;

export interface MarketingScopeResult {
  /** True when at least one valid consent row grants the marketing scope. */
  granted: boolean;
  /** Populated when granted=false to explain why (audit trail). */
  reason?: string;
}

/**
 * Rail 6: verify the lead has a consent record that grants the
 * MARKETING scope for the given channel. Used by the compliance gate
 * BEFORE sending a MARKETING-category template.
 *
 * Fail-closed semantics:
 *   * If the query errors, returns granted=false with a reason.
 *   * If no rows are found, returns granted=false.
 *   * If rows exist but `channel_scope` is NULL on all of them, returns
 *     granted=false (legacy rows are treated as service-only per the
 *     backfill migration).
 *   * If at least one non-expired row contains the marketing-scope
 *     token, returns granted=true.
 *
 * Callers should only invoke this when `template.category === 'MARKETING'`.
 */
export async function checkMarketingScope(
  adminClient: SupabaseClient,
  params: CheckMarketingScopeParams,
): Promise<MarketingScopeResult> {
  const logger = await getLogger();

  const ctx = {
    namespace: NAMESPACE,
    leadId: params.leadId,
    channel: params.channel,
  };

  const parsed = CheckMarketingScopeSchema.parse(params);

  const token = marketingScopeFor(parsed.channel);

  // Channels without a marketing-scope token (phone, instagram) are
  // outside Rail 6's purview. Treat as granted so the rail is a no-op.
  if (!token) {
    return { granted: true };
  }

  const { data, error } = await adminClient
    .from('consent_records')
    .select('id, expires_at, channel_scope')
    .eq('lead_id', parsed.leadId)
    .eq('account_id', parsed.accountId)
    .eq('channel', parsed.channel)
    .limit(100);

  if (error) {
    logger.error({ ...ctx, error }, 'Rail 6 marketing-scope query failed');
    return {
      granted: false,
      reason: 'marketing_scope_check_failed',
    };
  }

  if (!data || data.length === 0) {
    logger.info(ctx, 'Rail 6: no consent rows for lead + channel');
    return { granted: false, reason: 'no_consent_record' };
  }

  const now = new Date();

  const hasMarketing = data.some((row) => {
    const scopeRaw = (row as { channel_scope?: unknown }).channel_scope;
    const scope: string[] = Array.isArray(scopeRaw)
      ? (scopeRaw as unknown[]).filter(
          (v): v is string => typeof v === 'string',
        )
      : [];

    if (!scope.includes(token)) return false;

    const expiresAt = (row as { expires_at?: string | null }).expires_at;
    if (!expiresAt) return true;

    return new Date(expiresAt) > now;
  });

  if (!hasMarketing) {
    logger.info(
      { ...ctx, rowCount: data.length, token },
      'Rail 6: marketing scope not granted',
    );
    return { granted: false, reason: 'marketing_scope_not_granted' };
  }

  return { granted: true };
}
